import React from 'react';
import styles from '../../pages/DiagramPage/DiagramEditor.module.css';
import { Shape } from '../../types/diagram.types';
import { DEFAULT_SHAPE_PRESET, SHAPE_STYLE_PRESETS } from '../../config/shapePresets';

interface ShapeComponentProps {
  shape: Shape;
  isSelected: boolean;
  isEditMode: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseUp: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  onResizeStart: (
    e: React.MouseEvent,
    direction: 'se' | 'sw' | 'ne' | 'nw'
  ) => void;
}

export function ShapeComponent({
  shape,
  isSelected,
  isEditMode,
  onMouseDown,
  onMouseUp,
  onDoubleClick,
  onResizeStart, 
}: ShapeComponentProps) {
  const { width, height } = shape.size;

  const presetKey = shape.stylePreset ?? DEFAULT_SHAPE_PRESET;
  const presetStyle = SHAPE_STYLE_PRESETS[presetKey];

  const style = {
    ...presetStyle,
    ...(shape.style ?? {}),
  };

  const svgProps = {
    fill:
      style.fillStyle === 'gradient'
        ? `url(#grad-${shape.id})`
        : style.fillColor,

    stroke: style.borderColor,
    strokeWidth: style.borderWidth,
    strokeDasharray:
      style.borderStyle === 'dashed' ? '6,4' : undefined,

    opacity: style.opacity ?? 1,
    filter: style.shadow ? 'url(#shadow)' : undefined,
  };

  const renderShapePath = () => {
    switch (shape.type) {
      case 'rectangle':
        return (
          <rect
            width={width}
            height={height}
            rx={6}
            ry={6}
            {...svgProps}
          />
        );

      case 'circle':
        return (
          <ellipse
            cx={width / 2}
            cy={height / 2}
            rx={width / 2}
            ry={height / 2}
            {...svgProps}
          />
        );

      case 'diamond':
        return (
          <path
            d={`M ${width / 2} 0 L ${width} ${height / 2} L ${width / 2} ${height} L 0 ${height / 2} Z`}
            {...svgProps}
          />
        );

      case 'triangle':
        return (
          <path
            d={`M ${width / 2} 0 L ${width} ${height} L 0 ${height} Z`}
            {...svgProps}
          />
        );

      case 'hexagon': {
        const points = [
          [width / 2, 0],
          [width, height * 0.25],
          [width, height * 0.75],
          [width / 2, height],
          [0, height * 0.75],
          [0, height * 0.25],
        ];
        return (
          <path
            d={`M ${points.map(p => p.join(',')).join(' L ')} Z`}
            {...svgProps}
          />
        );
      }

      case 'parallelogram': {
        const offset = width * 0.2;
        return (
          <path
            d={`M ${offset} 0 L ${width} 0 L ${width - offset} ${height} L 0 ${height} Z`}
            {...svgProps}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      className={`${styles.shape} ${isSelected ? styles.shapeSelected : ''} ${!isEditMode ? styles.shapeViewMode : ''}`}
      style={{
        left: shape.position.x,
        top: shape.position.y,
        width,
        height,
        transform: `rotate(${shape.rotation}deg)`,
        cursor: isEditMode ? 'move' : 'default',
      }}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onDoubleClick={onDoubleClick}
    >
      <svg width={width} height={height} style={{ display: 'block' }}>
        <defs>
          {style.fillStyle === 'gradient' && (
            <linearGradient
              id={`grad-${shape.id}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={style.gradientFrom} />
              <stop offset="100%" stopColor={style.gradientTo} />
            </linearGradient>
          )}

          <filter id="shadow">
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="3"
              floodOpacity="0.25"
            />
          </filter>
        </defs>

        {renderShapePath()}
      </svg>

      {shape.text && (
        <div
          className={styles.shapeText}
          style={{
            fontSize: `${shape.fontSize}px`,
            transform: `rotate(-${shape.rotation}deg)`,
          }}
        >
          {shape.text}
        </div>
      )}

      {isSelected && isEditMode && (
        <>
          <div
            className={`${styles.resizeHandle} ${styles.resizeHandleNW}`}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeStart(e, 'nw');
            }}
          />

          <div
            className={`${styles.resizeHandle} ${styles.resizeHandleNE}`}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeStart(e, 'ne');
            }}
          />

          <div
            className={`${styles.resizeHandle} ${styles.resizeHandleSW}`}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeStart(e, 'sw');
            }}
          />

          <div
            className={`${styles.resizeHandle} ${styles.resizeHandleSE}`}
            onMouseDown={(e) => {
              e.stopPropagation();
              onResizeStart(e, 'se');
            }}
          />
        </>
      )}
    </div>
  );
};